// src/presentation/pages/ClinicalExamination/sections/FamilyHistory.tsx
import React from "react";
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

interface FamilyHistoryProps {
  value: boolean | null;
  relation: string;
  otherRelation: string;
  onChange: (value: boolean) => void;
  onRelationChange: (value: string) => void;
  onOtherRelationChange: (value: string) => void;
}

const getRelationOptions = (t: TFunction<"patients">) => [
  { value: "Parent", label: t("clinicalSections.familyHistory.relations.parent") },
  { value: "Sibling", label: t("clinicalSections.familyHistory.relations.sibling") },
  { value: "Child", label: t("clinicalSections.familyHistory.relations.child") },
  { value: "Grandparent", label: t("clinicalSections.familyHistory.relations.grandparent") },
  { value: "Aunt/Uncle", label: t("clinicalSections.familyHistory.relations.auntUncle") },
  { value: "Cousin", label: t("clinicalSections.familyHistory.relations.cousin") },
  { value: "Other", label: t("clinicalSections.familyHistory.relations.other") },
];

export const FamilyHistory: React.FC<FamilyHistoryProps> = ({
  value,
  relation,
  otherRelation,
  onChange,
  onRelationChange,
  onOtherRelationChange,
}) => {
  const { t } = useTranslation("patients");
  const showFields = value === true;
  const RELATION_OPTIONS = getRelationOptions(t);

  return (
    <div className="border-gray-200">
      <CardHeader className="pb-3 !px-0">
        <CardTitle className="text-base font-semibold text-gray-900">
          {t("clinicalSections.familyHistory.title")}
        </CardTitle>
        <p className="text-sm text-gray-500">
          {t("clinicalSections.familyHistory.question")}
        </p>
      </CardHeader>
      <CardContent className=" space-y-4 !px-0">
        <RadioGroup
          value={value?.toString()}
          onValueChange={(val) => onChange(val === "true")}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="true" id="family-yes" />
            <Label
              htmlFor="family-yes"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.yes")}
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="false" id="family-no" />
            <Label
              htmlFor="family-no"
              className="text-[#1B1D21] cursor-pointer text-sm font-normal"
            >
              {t("clinicalSections.no")}
            </Label>
          </div>
        </RadioGroup>

        {showFields && (
          <div className="    border-primary/30">
            <Label htmlFor="relation" className="text-[#5F646F]">
              {t("clinicalSections.familyHistory.relationLabel")}
            </Label>
            <Select value={relation} onValueChange={onRelationChange}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={t("clinicalSections.familyHistory.selectRelation")} />
              </SelectTrigger>
              <SelectContent
                position="popper"
                side="bottom"
                align="start"
                sideOffset={4}
                className="z-50 cursor-pointer"
              >
                {RELATION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {relation === "Other" && (
              <div className="mt-3">
                <Label htmlFor="other-relation" className="text-sm font-medium">
                  {t("clinicalSections.familyHistory.pleaseSpecify")}
                </Label>
                <Input
                  id="other-relation"
                  placeholder={t("clinicalSections.familyHistory.otherRelationPlaceholder")}
                  value={otherRelation}
                  onChange={(e) => onOtherRelationChange(e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </div>
  );
};
